import NiceModal from '@ebay/nice-modal-react';

import { useMemo, useState } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import { Trans } from 'react-i18next';

import Checkbox from 'components/global/Checkbox';
import Collapse from 'components/global/Collapse';
import Radio from 'components/global/Radio';
import Skeleton from 'components/global/Skeleton';
import Typography from 'components/global/Typography';
import ChargeApplePay from 'components/payment/tap/ChargeApplePay';
import ChargeTapPayment from 'components/payment/tap/ChargeTapPayment';
import TapIframe from 'components/payment/tap/TapIframe';
import getTapPaymentUserPhone from 'components/payment/tap/getTapPaymentUserPhone';

import { LinkViewer } from 'pages/ImportOrders/ImportOrdersList.styled';
import PrivacyPolicyModal from 'pages/Login/PrivacyPolicyModal';

import { OTO_LINK } from 'services/consts';

import { FormHeader } from '../OTOLinkBuyer';
import * as S from './Tabs.styled';

const validateOptions = {
	shouldValidate: true,
	shouldDirty: true
};

const PAYMENT_METHODS = {
	CREDIT_CARD: 'creditCard',
	APPLE_PAY: 'applePay'
};

const PaymentTab = ({ onPaymentSuccess, prevStep }) => {
	const formMethods = useFormContext();
	const [isIframeLoaded, setIsIframeLoaded] = useState(false);
	const [inputsValid, setInputsValid] = useState(false);
	const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(PAYMENT_METHODS.CREDIT_CARD);
	const {
		setValue,
		watch,
		trigger,
		formState: { isSubmitting, errors }
	} = formMethods;

	const chargePaymentPayload = useMemo(() => {
		const userPhone = getTapPaymentUserPhone(watch('phone'));
		return {
			key: watch('key'),
			GUID: watch('GUID'),
			otoLinkSessionId: watch('otoLinkSessionId'),
			amount: watch('grandTotal'),
			code: null,
			countryCode: userPhone.countryCode,
			phone: userPhone.phone,
			saveCard: false,
			customer: {
				name: `${watch('firstName')} ${watch('lastName')}`,
				email: watch('email')
			}
		};
	}, []);

	const changePaymentMethod = (method) => {
		setSelectedPaymentMethod(method);
	};

	return (
		<FormProvider {...formMethods}>
			<S.Card padding={0} background border={0} shadowed rounded>
				<S.Wrapper>
					<div>
						<FormHeader
							title="enterCreditCardDetails"
							className="section-padding-bottom"
							onClick={() => prevStep()}
						/>
						<S.PaymentCollapse
							id={PAYMENT_METHODS.CREDIT_CARD}
							onClick={() => changePaymentMethod(PAYMENT_METHODS.CREDIT_CARD)}>
							<Radio checked={selectedPaymentMethod === PAYMENT_METHODS.CREDIT_CARD} readOnly />
							<Typography variant="p1" component="span" fontWeight={500}>
								creditcard
							</Typography>
						</S.PaymentCollapse>

						<Collapse
							visible={selectedPaymentMethod === PAYMENT_METHODS.CREDIT_CARD}
							invisibleMode
							defaultVisible>
							{selectedPaymentMethod === PAYMENT_METHODS.CREDIT_CARD && !isIframeLoaded && (
								<Skeleton width="100%" height="5rem" className="skeleton" />
							)}
							<TapIframe
								subscription={true}
								setInputsValid={setInputsValid}
								setIsIframeLoaded={setIsIframeLoaded}
								paymentGatewayType={watch('paymentProvider')}
							/>
						</Collapse>

						{window.ApplePaySession && (
							<>
								<S.PaymentCollapse
									id={PAYMENT_METHODS.APPLE_PAY}
									onClick={() => changePaymentMethod(PAYMENT_METHODS.APPLE_PAY)}>
									<Radio checked={selectedPaymentMethod === PAYMENT_METHODS.APPLE_PAY} readOnly />
									<Typography variant="p1" component="span" fontWeight={500}>
										applePay
									</Typography>
								</S.PaymentCollapse>
								<Collapse visible={selectedPaymentMethod === PAYMENT_METHODS.APPLE_PAY} defaultVisible>
									<S.ApplePayButtonContainer>
										{!watch('termsAndPrivacy') && (
											<S.ApplePayButtonOverlay onClick={() => trigger('termsAndPrivacy')} />
										)}
										<ChargeApplePay
											onComplete={(data) => onPaymentSuccess(data)}
											body={chargePaymentPayload}
											paymentGatewayType={watch('paymentProvider')}
											isKYCPayment
										/>
									</S.ApplePayButtonContainer>
								</Collapse>
							</>
						)}
					</div>

					<S.FormRow id="confirm-form">
						<S.CheckboxContainer hasError={!!errors?.termsAndPrivacy?.message}>
							<Checkbox
								id="termsAndPrivacy"
								checked={watch('termsAndPrivacy')}
								onChange={(e) => setValue('termsAndPrivacy', e?.target?.checked, validateOptions)}
								error={errors?.termsAndPrivacy?.message}
							/>
							<Typography
								color="var(--content-3)"
								className="terms-and-privacy"
								noWrap
								onClick={() => setValue('termsAndPrivacy', !watch('termsAndPrivacy'), validateOptions)}>
								<Trans
									i18nKey="otoLinkPrivacyPolicy"
									components={{
										linkViewer: (
											<LinkViewer
												onClick={(e) => {
													NiceModal.show(PrivacyPolicyModal);
													e.stopPropagation();
												}}
											/>
										)
									}}
								/>
							</Typography>
						</S.CheckboxContainer>

						{selectedPaymentMethod === PAYMENT_METHODS.CREDIT_CARD && (
							<ChargeTapPayment
								buttonProps={{
									label: 'completePayment',
									variant: 'blue',
									fullWidth: true,
									size: 'xx-large',
									loading: isSubmitting,
									disabled: !isIframeLoaded || !inputsValid || !watch('termsAndPrivacy')
								}}
								onComplete={(data) => onPaymentSuccess(data)}
								apiURL={OTO_LINK.MAKE_CHARGE}
								body={chargePaymentPayload}
							/>
						)}
					</S.FormRow>
				</S.Wrapper>
			</S.Card>
		</FormProvider>
	);
};

export default PaymentTab;
